import { z } from 'zod';

/**
 * The principal kinds platform."User".userType can hold. This is the
 * authorization discriminator: `admin` is the tenant administrator and
 * `superuser` the cross-tenant platform operator. The tenant user-management
 * surface derives a narrower variant (see features/users/user.ts) because a
 * superuser is never created there.
 */
export const UserTypeSchema = z.enum(['superuser', 'admin', 'user']);
export type UserType = z.infer<typeof UserTypeSchema>;

/**
 * How much of the tenant a `user` principal's lists show — nested, widest
 * last. `self` sees only their own book, `branch` their branches', `tenant`
 * everything. Admins are tenant-wide regardless.
 */
export const AccessTierSchema = z.enum(['self', 'branch', 'tenant']);
export type AccessTier = z.infer<typeof AccessTierSchema>;

export const PermissionScopeAccessSchema = z.object({
  scope: z.string(),
  canRead: z.boolean(),
  canCreate: z.boolean(),
  canUpdate: z.boolean(),
  canDelete: z.boolean(),
  // Granular lifecycle actions, grouped by the scope they hang off.
  // `.default(false)` keeps payloads that omit a field parsing cleanly, and
  // fails closed while it does.
  //
  // device_units
  canTransfer: z.boolean().default(false),
  canUpdateExpiration: z.boolean().default(false),
  canIssueOfflinePin: z.boolean().default(false),
  canSyncState: z.boolean().default(false),
  canResetState: z.boolean().default(false),
  canProcessReturn: z.boolean().default(false),
  canWriteOff: z.boolean().default(false),
  canLock: z.boolean().default(false),
  canUnlock: z.boolean().default(false),
  canRemoveLock: z.boolean().default(false),
  // applications
  canApprove: z.boolean().default(false),
  canReject: z.boolean().default(false),
  // applications + customers
  canVerifyKyc: z.boolean().default(false),
  // contracts
  canActivate: z.boolean().default(false),
  canCancel: z.boolean().default(false),
  canReinstate: z.boolean().default(false),
  // payments
  canReverse: z.boolean().default(false),
});
export type PermissionScopeAccess = z.infer<typeof PermissionScopeAccessSchema>;

const PermissionScopeSchema = z.object({
  scope: z.string(),
  name: z.string(),
  description: z.string(),
  actions: z.array(z.string()),
  translations: z.record(z.unknown()),
});
export type PermissionScope = z.infer<typeof PermissionScopeSchema>;

const AuthUserRoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  isGeneric: z.boolean(),
  bypassPermissions: z.boolean(),
});

const AuthUserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  // Platform-service /auth/login currently returns only id/email/firstName/lastName
  // (see services/platform-service/app/schemas/auth.py:OperatorSummary). The
  // fields below are not yet on the wire; they default to safe absent values
  // until platform exposes a /auth/me endpoint that includes them.
  phoneNumber: z.string().nullable().default(null),
  tenantId: z.string().nullable().default(null),
  branchId: z.string().nullable().default(null),
  isActive: z.boolean().default(true),
  // userType is the canonical authorization discriminator, stored on
  // platform."User" — administrative access is decided from it. The `role`
  // object below is a compatibility projection the backend synthesises from
  // the same value; nothing should gate on its name.
  //
  // Absent means "not yet known" (the bare login response carries no userType;
  // /auth/me does). It must therefore read as the least privileged answer
  // everywhere — see lib/auth/roles.ts.
  userType: UserTypeSchema.nullable().default(null),
  // How much of the tenant this operator's *lists* show. Nested widest-last:
  // self ⊂ branch ⊂ tenant. Defaults to the narrowest so a payload without it
  // can never widen what the UI offers.
  accessTier: AccessTierSchema.default('self'),
  // Which role template produced the permission matrix, or null once it has
  // been hand-edited — that null is what the user list renders as "custom".
  roleTemplateId: z.string().nullable().default(null),
  role: AuthUserRoleSchema.nullable().default(null),
  permissions: z.array(PermissionScopeAccessSchema).default([]),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

// Platform's bare /auth/login response (NOT wrapped in OperationResult).
// See services/platform-service/app/schemas/auth.py.
const PlatformLoginResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.string().default('bearer'),
  expiresIn: z.number(),
  operator: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  }),
});
export type PlatformLoginResponse = z.infer<typeof PlatformLoginResponseSchema>;

// The shape login.tsx destructures. We synthesize this from the platform
// response inside api.ts:login so call sites don't need to change.
// `refreshToken` is optional: the cookie-based flow (T10/T11) delivers the
// refresh token as an HttpOnly cookie and it never appears in this synthesized
// LoginResponse object. Kept as `.optional()` rather than removed for
// TypeScript structural compatibility with any pre-T13 call site that
// assigned a refreshToken-carrying value to this type.
const LoginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  tokenType: z.string(),
  expiresIn: z.number(),
  user: AuthUserSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
